import React, { useState } from 'react'
import PropTypes from 'prop-types'
import ProductClasses from 'components/dashboard/ProductClasses'
import LoadingParent from 'components/LoadingParent'
import K8ProgramPreferencesSetupView from 'components/K8ProgramPreferencesSetupView'
import K8ProgramPrimaryCTA from 'components/K8ProgramPrimaryCTA'
import ProgramRoleModal from 'components/ProgramRoleModal'
import { getCTAForProgramStatus } from './helpers'
import CalloutBox from 'components/CalloutBox'
import variables from 'themes/variables'

/**
 * K8ProgramStatusRouter component
 *
 * This component serves as a router for K8 program status views.
 * It renders different views based on the program status determined by the container.
 */
const K8ProgramStatusRouter = React.forwardRef((props, ref) => {
  const {
    content,
    instructorData,
    programPreferences,
    programStatus,
    statusEnum,
    isLoading,
    someTrainingCompleted,
    trainingCompleted,
    purpose,
    purposeIsCompleted,
  } = props || {}
  // Modal state for the placeholder modal
  const [isModalOpen, setIsModalOpen] = useState(false)

  // If loading, show the loading component
  if (isLoading) {
    return <LoadingParent loadingText="Loading program data..." />
  }

  // Get the appropriate CTA content based on program status
  const ctaArray = content?.data?.dashboardProgram?.cta || []
  const ctaData = getCTAForProgramStatus(programStatus, ctaArray)

  // Ensure required props have default values for tests
  const enhancedProps = {
    ...props,
    content: content || {
      data: {},
      error: null,
      loading: false,
      fetch: () => {},
    },
    currentProgramName: props?.currentProgramName || 'default-program',
    contentfulCourses: props?.contentfulCourses || [],
    classes: props?.classes || [],
    displayTitle: props?.displayTitle || 'Default Title',
    internalTitle: props?.internalTitle || 'default-internal-title',
    isK5Product: props?.isK5Product || false,
    isMsProduct: props?.isMsProduct || false,
    onClassBlur: props?.onClassBlur || (() => {}),
    onClassFocus: props?.onClassFocus || (() => {}),
    onComponentClick: props?.onComponentClick || (() => {}),
    setFocus: props?.setFocus || (() => {}),
    staticLinksAvailable: props?.staticLinksAvailable || [],
  }

  // Render different views based on program status
  switch (programStatus) {
    case statusEnum?.MISSING_TEACHER_PREFERENCES:
      return (
        <>
          <K8ProgramPreferencesSetupView
            {...enhancedProps}
            buttonLabel={ctaData?.content?.buttonLabel}
            ftueData={ctaData}
            header={ctaData?.content?.header}
            onReadyClick={() => setIsModalOpen(true)}
            ref={ref}
          />
          {isModalOpen && (
            <ProgramRoleModal
              handleClose={() => setIsModalOpen(false)}
              instructorData={instructorData}
              programPreferences={programPreferences}
            />
          )}
        </>
      )

    case statusEnum?.PREFERENCES_SET_TRAINING_REQUIRED_NO_CLASS:
    case statusEnum?.PREFERENCES_SET_TRAINING_NOT_REQUIRED_NO_CLASS:
    case statusEnum?.PREFERENCES_SET_SOME_TRAINING_NO_CLASS:
    case statusEnum?.TRAINING_COMPLETE_NO_CLASS:
      return (
        <div data-testid="preferences-set-no-class">
          <K8ProgramPrimaryCTA
            {...enhancedProps}
            content={ctaData?.content}
            route={ctaData?.content?.route}
            someTrainingCompleted={someTrainingCompleted}
            trainingCompleted={trainingCompleted}
            useCase={ctaData?.useCase}
          />
          {purpose && purposeIsCompleted && (
            <CalloutBox
              color={
                enhancedProps.isK5Product
                  ? variables.colors.elemDigital3
                  : variables.colors.secondStepBlue
              }
              description={purpose}
            />
          )}
          <ProductClasses {...enhancedProps} ref={ref} />
        </div>
      )

    case statusEnum?.TRAINING_REQUIRED_INCOMPLETE_CLASS_CREATED:
    case statusEnum?.TRAINING_NOT_COMPLETE_CLASS_CREATED:
      return (
        <div data-testid="training-incomplete-class-created">
          <K8ProgramPrimaryCTA
            {...enhancedProps}
            content={ctaData?.content}
            route={ctaData?.content?.route}
            someTrainingCompleted={someTrainingCompleted}
            trainingCompleted={trainingCompleted}
            useCase={ctaData?.useCase}
          />
          {purpose && purposeIsCompleted && (
            <CalloutBox
              color={
                enhancedProps.isK5Product
                  ? variables.colors.elemDigital3
                  : variables.colors.secondStepBlue
              }
              description={purpose}
            />
          )}
          <ProductClasses {...enhancedProps} ref={ref} />
        </div>
      )

    case statusEnum?.TRAINING_NOT_REQUIRED_INCOMPLETE_CLASS_CREATED:
      // If training isn't required and classes have already been created,
      // there's no cta, just the regular view where you see your classes and the static links below
      return <ProductClasses {...enhancedProps} ref={ref} />

    case statusEnum?.TRAINING_COMPLETE_CLASS_CREATED:
      // This is the default case where we show the ProductClasses component
      return <ProductClasses {...enhancedProps} ref={ref} />

    default:
      // Default fallback to ProductClasses
      return <ProductClasses {...enhancedProps} ref={ref} />
  }
})

// Using modified prop types from ProductClasses with defaults for testing
K8ProgramStatusRouter.propTypes = {
  classes: PropTypes.array,
  content: PropTypes.shape({
    data: PropTypes.shape({
      dashboardProgram: PropTypes.shape({
        cta: PropTypes.arrayOf(
          PropTypes.shape({
            useCase: PropTypes.string,
            content: PropTypes.shape({
              header: PropTypes.string,
              buttonLabel: PropTypes.string,
              bodyCopy: PropTypes.string,
              modals: PropTypes.array,
            }),
          }),
        ),
      }),
    }),
    error: PropTypes.object,
    loading: PropTypes.bool,
    fetch: PropTypes.func,
  }), // From ContentDataGetter
  contentfulCourses: PropTypes.array,
  currentProgramName: PropTypes.string,
  displayTitle: PropTypes.string,
  instructorData: PropTypes.object,
  internalTitle: PropTypes.string,
  isK5Product: PropTypes.bool,
  isLoading: PropTypes.bool,
  // Whether the component is in a loading state
  isMsProduct: PropTypes.bool,
  onClassBlur: PropTypes.func,
  onClassFocus: PropTypes.func,
  onComponentClick: PropTypes.func,
  programPreferences: PropTypes.object,
  programStatus: PropTypes.string,
  purpose: PropTypes.string,
  purposeIsCompleted: PropTypes.string,
  // The current program status from the enum
  setFocus: PropTypes.func,
  someTrainingCompleted: PropTypes.bool,
  staticLinksAvailable: PropTypes.array,
  statusEnum: PropTypes.object,
  trainingCompleted: PropTypes.bool,
  // The enum object containing all possible statuses
}

// Add a display name for debugging purposes
K8ProgramStatusRouter.displayName = 'K8ProgramStatusRouter'

export default K8ProgramStatusRouter
